import {
  BlindKingsGame,
  PIECE_VALUES,
  type BlindKingsGame as BlindKingsGameType,
} from '@blind-kings/game-core';
import {
  ARMY_SIZE,
  BOARD_H,
  BOARD_W,
  CELL_COUNT,
  CHANNEL,
  GLOBAL_SIZE,
  MAX_CP,
  NUM_ACTION_PLANES,
  PROGRESS_SCALE,
  SPATIAL_CHANNELS,
  TOTAL_MATERIAL,
} from './constants';
import { actionMask, cellIndex } from './actions';
import { actingSeat, lastActionWasSeat, rivalSeat } from './perspective';

type BoardPiece = {
  location: { y: number; x: number };
  id: number;
  value: number;
  teamIndex: number;
  isEmpty?: boolean;
  isUnturned?: boolean;
  isActive?: boolean;
  isAssassin?: boolean;
  canAttackByDirection: (direction: string) => boolean;
  getPieceToAttackByDirection?: (direction: string) => BoardPiece | undefined;
  getJumpedPieceByDirection?: (direction: string) => BoardPiece | undefined;
};

type EncodeGame = BlindKingsGameType & {
  getPlayerHighestPiece: (seat: number) => { value: number };
  getPlayerFollowing: (seat: number) =>
    | { pieceId?: number; following?: Record<string, number> }
    | undefined;
  followingTooLongMap?: Record<string, unknown>;
  completeGamePieceList: unknown[];
  allPotentialUnturnedPieces: unknown[];
  getPlayerUnturnedPieces: (seat: number) => { value: number }[];
  getPlayerActivePieces: (seat: number) => unknown[];
  getPlayerGraveyard: (seat: number) => { value: number }[];
  maxTurnsWithoutAction: number;
  turnsWithoutAction: number;
  maxFollowingTurns: number;
  turnNumber: number;
  lastAction?: { from?: { x: number; y: number }; to?: { x: number; y: number } };
  gameBoard: {
    allPieces: BoardPiece[];
    gamePiecesById?: Record<number, BoardPiece>;
  };
};

export type EncodedState = {
  spatial: Float32Array;
  global: Float32Array;
  seat: 0 | 1;
};

function setPlane(
  spatial: Float32Array,
  channel: number,
  y: number,
  x: number,
  value: number
): void {
  spatial[channel * BOARD_H * BOARD_W + cellIndex(y, x)] = value;
}

function setCell(spatial: Float32Array, channel: number, cell: number, value: number): void {
  spatial[channel * CELL_COUNT + cell] = value;
}

function countsByCp(pieces: { value: number }[]): number[] {
  const counts = Array.from({ length: MAX_CP }, () => 0);
  for (const { value } of pieces) {
    if (value >= 1 && value <= MAX_CP) counts[value - 1] += 1;
  }
  return counts;
}

function materialByCp(counts: number[]): number {
  let sum = 0;
  for (let i = 0; i < counts.length; i += 1) sum += (i + 1) * counts[i];
  return sum;
}

/** Highest CP still alive: revealed board pieces plus remaining pool. */
export function highestAlive(game: EncodeGame, seat: number): number {
  let high = 0;
  for (const piece of game.getPlayerUnturnedPieces(seat)) {
    if (piece.value > high && piece.value <= MAX_CP) high = piece.value;
  }
  for (const piece of game.getPlayerActivePieces(seat) as { value: number }[]) {
    if (piece.value > high && piece.value <= MAX_CP) high = piece.value;
  }
  return high;
}

/**
 * Stall-clock winner from `seat`'s view: more revealed pieces, then highest
 * alive (pool + board), then who acted last. Matches v2 `stall_resolution`.
 */
export function stallResolutionValue(game: EncodeGame, me: 0 | 1): number {
  const rival = rivalSeat(me);
  const myActive = game.getPlayerActivePieces(me).length;
  const rivalActive = game.getPlayerActivePieces(rival).length;
  if (myActive !== rivalActive) return myActive > rivalActive ? 1 : -1;

  const myHigh = highestAlive(game, me);
  const rivalHigh = highestAlive(game, rival);
  if (myHigh !== rivalHigh) return myHigh > rivalHigh ? 1 : -1;

  return lastActionWasSeat(game, me) ? 1 : -1;
}

export function encodeState(gameInput: BlindKingsGameType): EncodedState {
  const game = gameInput as EncodeGame;
  const me = actingSeat(game);
  const rival = rivalSeat(me);
  const spatial = new Float32Array(SPATIAL_CHANNELS * BOARD_H * BOARD_W);
  const global = new Float32Array(GLOBAL_SIZE);
  const mask = actionMask(game);

  let ownBoardMaterial = 0;
  let enemyBoardMaterial = 0;

  for (const piece of game.gameBoard.allPieces) {
    const { y, x } = piece.location;
    if (piece.isEmpty) {
      setPlane(spatial, CHANNEL.EMPTY, y, x, 1);
      continue;
    }
    if (piece.isUnturned) {
      setPlane(spatial, CHANNEL.UNTURNED, y, x, 1);
      continue;
    }
    if (!piece.isActive) continue;
    const cp = piece.value;
    if (cp < 1 || cp > MAX_CP) continue;
    const own = piece.teamIndex === me;
    const base = own ? CHANNEL.OWN_CP0 : CHANNEL.ENEMY_CP0;
    const scalar = own ? CHANNEL.OWN_CP_SCALAR : CHANNEL.ENEMY_CP_SCALAR;
    setPlane(spatial, base + (cp - 1), y, x, 1);
    setPlane(spatial, scalar, y, x, cp / MAX_CP);
    if (own) ownBoardMaterial += cp;
    else enemyBoardMaterial += cp;
  }

  const maxFollow = game.maxFollowingTurns > 0 ? game.maxFollowingTurns : 4;
  const tooLong = game.followingTooLongMap ?? {};
  for (const seat of [0, 1] as const) {
    const sign = seat === me ? 1 : -1;
    const chase = game.getPlayerFollowing(seat);
    const chaserId = Number(chase?.pieceId);
    const chaser = Number.isFinite(chaserId)
      ? game.gameBoard.gamePiecesById?.[chaserId]
      : undefined;
    if (chase?.following) {
      for (const [oppId, turns] of Object.entries(chase.following)) {
        const chased = game.gameBoard.gamePiecesById?.[Number(oppId)];
        if (!chased?.location) continue;
        setPlane(
          spatial,
          CHANNEL.CHASE_INTENSITY,
          chased.location.y,
          chased.location.x,
          sign * (Number(turns) / maxFollow)
        );
      }
    }
    if (chaser?.location && tooLong[String(chaserId)] !== undefined) {
      setPlane(
        spatial,
        CHANNEL.CHASE_BLOCKED,
        chaser.location.y,
        chaser.location.x,
        sign
      );
    }
  }

  for (let plane = 0; plane < NUM_ACTION_PLANES; plane += 1) {
    const channel = CHANNEL.LEGAL_MASK + plane;
    for (let cell = 0; cell < CELL_COUNT; cell += 1) {
      const v = mask[plane * CELL_COUNT + cell];
      if (v) setCell(spatial, channel, cell, v);
    }
  }

  const cheapest = new Float32Array(CELL_COUNT);
  for (const piece of game.gameBoard.allPieces) {
    if (!piece.isActive || piece.isEmpty || piece.isUnturned) continue;
    const cp = piece.value;
    if (cp < 1 || cp > MAX_CP) continue;
    const own = piece.teamIndex === me;
    for (const direction of ['UP', 'DOWN', 'LEFT', 'RIGHT']) {
      if (!piece.canAttackByDirection(direction)) continue;
      const target = piece.getPieceToAttackByDirection?.(direction);
      if (!target?.location) continue;
      const cell = cellIndex(target.location.y, target.location.x);
      const captureCh = own ? CHANNEL.OWN_CAN_CAPTURE : CHANNEL.ENEMY_CAN_CAPTURE;
      setPlane(spatial, captureCh, target.location.y, target.location.x, 1);
      if (!own && (cheapest[cell] === 0 || cp < cheapest[cell])) {
        cheapest[cell] = cp;
      }
    }
  }
  for (let cell = 0; cell < CELL_COUNT; cell += 1) {
    if (cheapest[cell] > 0) {
      setCell(spatial, CHANNEL.CHEAPEST_ENEMY_ATTACKER, cell, cheapest[cell] / MAX_CP);
    }
  }

  for (const piece of game.gameBoard.allPieces) {
    if (!piece.isAssassin || !piece.isActive) continue;
    const own = piece.teamIndex === me;
    const channel = own ? CHANNEL.OWN_ASSASSIN_REACH : CHANNEL.ENEMY_ASSASSIN_REACH;
    for (const direction of ['UP', 'DOWN', 'LEFT', 'RIGHT']) {
      const landed = piece.getJumpedPieceByDirection?.(direction);
      if (!landed?.location) continue;
      setPlane(spatial, channel, landed.location.y, landed.location.x, 1);
    }
  }

  const last = game.lastAction;
  if (last?.from && Number.isFinite(last.from.x) && Number.isFinite(last.from.y)) {
    setPlane(spatial, CHANNEL.LAST_ACTION_FROM, last.from.y, last.from.x, 1);
  }
  if (last?.to && Number.isFinite(last.to.x) && Number.isFinite(last.to.y)) {
    setPlane(spatial, CHANNEL.LAST_ACTION_TO, last.to.y, last.to.x, 1);
  }

  let g = 0;
  const ownPool = countsByCp(game.getPlayerUnturnedPieces(me));
  const enemyPool = countsByCp(game.getPlayerUnturnedPieces(rival));
  const ownGy = countsByCp(game.getPlayerGraveyard(me));
  const enemyGy = countsByCp(game.getPlayerGraveyard(rival));
  for (const c of ownPool) global[g++] = c / ARMY_SIZE;
  for (const c of enemyPool) global[g++] = c / ARMY_SIZE;
  for (const c of ownGy) global[g++] = c / ARMY_SIZE;
  for (const c of enemyGy) global[g++] = c / ARMY_SIZE;

  const ownActive = game.getPlayerActivePieces(me).length;
  const enemyActive = game.getPlayerActivePieces(rival).length;
  const ownHigh = highestAlive(game, me);
  const enemyHigh = highestAlive(game, rival);
  global[g++] = ownActive / ARMY_SIZE;
  global[g++] = enemyActive / ARMY_SIZE;
  global[g++] = ownHigh / MAX_CP;
  global[g++] = enemyHigh / MAX_CP;

  const ownPoolN = ownPool.reduce((a, b) => a + b, 0);
  const enemyPoolN = enemyPool.reduce((a, b) => a + b, 0);
  const totalPool = ownPoolN + enemyPoolN;
  const firstFlip = totalPool === 2 * ARMY_SIZE;
  const flipTotal = firstFlip ? ownPoolN : totalPool;
  global[g++] = flipTotal / CELL_COUNT;
  if (flipTotal > 0) {
    global[g++] = firstFlip ? 1 : ownPoolN / totalPool;
    const expected = firstFlip
      ? materialByCp(ownPool) / flipTotal
      : (materialByCp(ownPool) + materialByCp(enemyPool)) / totalPool;
    global[g++] = expected / MAX_CP;
  } else {
    global[g++] = 0;
    global[g++] = 0;
  }

  const stallNorm =
    game.maxTurnsWithoutAction > 0
      ? game.turnsWithoutAction / game.maxTurnsWithoutAction
      : 0;
  global[g++] = stallNorm;
  global[g++] = firstFlip ? 1 : 0;
  global[g++] = lastActionWasSeat(game, me) ? 1 : 0;
  global[g++] = Math.sign(ownActive - enemyActive);
  global[g++] = Math.sign(ownHigh - enemyHigh);
  global[g++] = stallResolutionValue(game, me);
  global[g++] = Math.min(game.turnNumber / PROGRESS_SCALE, 1);

  let emptyCount = 0;
  let unturnedCount = 0;
  for (const piece of game.gameBoard.allPieces) {
    if (piece.isEmpty) emptyCount += 1;
    else if (piece.isUnturned) unturnedCount += 1;
  }
  global[g++] = emptyCount / CELL_COUNT;
  global[g++] = unturnedCount / CELL_COUNT;
  global[g++] = ownBoardMaterial / TOTAL_MATERIAL;
  global[g++] = enemyBoardMaterial / TOTAL_MATERIAL;
  global[g++] = (ownBoardMaterial + materialByCp(ownPool)) / TOTAL_MATERIAL;
  global[g++] = (enemyBoardMaterial + materialByCp(enemyPool)) / TOTAL_MATERIAL;

  if (g !== GLOBAL_SIZE) {
    throw new Error(`Global feature length mismatch: wrote ${g}, expected ${GLOBAL_SIZE}`);
  }

  return { spatial, global, seat: me };
}

export function encodeBlindKingsGame(game: BlindKingsGame): EncodedState {
  return encodeState(game);
}

export { PIECE_VALUES };
